import React, { useState, useEffect, useRef, useCallback } from "react";
import Styles from "./ContractCalculator.module.css";
import {
  ArrowRightAlt,
  FileDownload,
  ChevronRight,
  ExpandMore,
  Edit,
} from "@mui/icons-material";
import ReactECharts from "echarts-for-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchContractAnalytics,
  fetchContractReturnAnalytics,
} from "../../Redux/Slices/BankingCalcSlice";

/* ── Constants ──────────────────────────────────────────── */
const GROUP_BY = [
  { key: "block", label: "Time Block" },
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
];

// Ported from insights ReMixChart — shared axis/brush accent color.
const SECONDARY_COLOR = "#34656D";
const AXIS_LINE_COLOR = "#E0E0E0";
const AXIS_LABEL_COLOR = "#8C8A96";

// Series colors — original domain palette (orange = actual, blue = scheduled).
const SERIES_COLORS = {
  actual: "#ff9800",
  scheduled: "#2E90FA",
};

/* ── Helpers ────────────────────────────────────────────── */
const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
};

const capitalizeFirst = (str) => {
  if (!str) return "—";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const fmt = (val, dec = 2) => {
  if (val == null || val === "") return "—";
  return Number(val).toLocaleString("en-IN", {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
};

// Compact y-axis value formatter (K / M / B) — ported from insights ReMixChart.
const formatAxisValue = (value) => {
  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (absValue >= 1_000_000_000) {
    const v = absValue / 1_000_000_000;
    return (
      sign + (v >= 10 ? v.toFixed(0) : v.toFixed(1)).replace(/\.0$/, "") + "B"
    );
  } else if (absValue >= 1_000_000) {
    const v = absValue / 1_000_000;
    return (
      sign + (v >= 10 ? v.toFixed(0) : v.toFixed(1)).replace(/\.0$/, "") + "M"
    );
  } else if (absValue >= 1_000) {
    const v = absValue / 1_000;
    return (
      sign + (v >= 10 ? v.toFixed(0) : v.toFixed(1)).replace(/\.0$/, "") + "K"
    );
  } else if (absValue < 10 && absValue > 0) {
    if (absValue < 0.01) return value.toFixed(4);
    else if (absValue < 0.1) return value.toFixed(3);
    else if (absValue < 1) return value.toFixed(2);
    else return value.toFixed(1);
  } else if (value === 0) return "0";
  else if (value % 1 === 0) return sign + Math.round(absValue).toString();
  return value.toFixed(1);
};

const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/* Enhanced tooltip HTML — ported from insights ReMixChart (customTooltipBuilder):
   frosted-glass card, SNAPSHOT kicker, large title, per-series rows with a
   vertical gradient accent bar and a boxed unit pill. Fully self-contained via
   an inline <style> block so it needs no external CSS. */
const buildTooltipHTML = ({ title, subtitle, rows, unit, fontSize = 12 }) => {
  const items = rows
    .map((r) => {
      const value = Number(r.value ?? 0)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      const color = escapeHtml(r.color);
      return `<div class="cc-tt-item" style="--accent:${color}"><span class="cc-tt-bar"></span><span class="cc-tt-name">${escapeHtml(
        r.name,
      )}</span><span class="cc-tt-vwrap"><span class="cc-tt-value">${value}</span><span class="cc-tt-unit">${escapeHtml(
        unit,
      )}</span></span></div>`;
    })
    .join("");

  const subtitleHtml = subtitle
    ? `<span class="cc-tt-sub">${escapeHtml(subtitle)}</span>`
    : "";

  const css = `<style>
.cc-tt-shell{padding:0;pointer-events:inherit}
.cc-tt{background:rgba(255,255,255,0.92);backdrop-filter:saturate(140%) blur(14px);-webkit-backdrop-filter:saturate(140%) blur(14px);box-shadow:0 1px 2px rgba(15,23,42,0.06),0 8px 24px -6px rgba(15,23,42,0.18),0 24px 48px -12px rgba(15,23,42,0.16);border:1px solid rgba(15,23,42,0.08);border-radius:14px;font-family:'Inter','SF Pro Text',system-ui,-apple-system,sans-serif;min-width:220px;max-width:360px;padding:14px 14px 10px;color:#334155;letter-spacing:-0.005em}
.cc-tt-header{display:flex;flex-direction:column;gap:8px;padding:0 2px 12px;margin-bottom:6px;border-bottom:1px dashed rgba(15,23,42,0.09)}
.cc-tt-kicker{display:inline-flex;align-items:center;gap:5px;font-size:${Math.max(fontSize - 3, 9)}px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;color:#64748b}
.cc-tt-kicker-dot{width:5px;height:5px;border-radius:50%;background:linear-gradient(135deg,#10b981,#059669);box-shadow:0 0 0 3px rgba(16,185,129,0.15)}
.cc-tt-title-stack{display:flex;align-items:baseline;gap:8px;min-width:0;flex-wrap:wrap}
.cc-tt-title{font-weight:650;font-size:${fontSize + 5}px;color:#334155;line-height:1.15;letter-spacing:-0.02em;font-variant-numeric:tabular-nums}
.cc-tt-sub{font-size:${fontSize}px;font-weight:500;color:#94a3b8;letter-spacing:-0.005em}
.cc-tt-content{display:flex;flex-direction:column;gap:0;margin:0 -4px;padding:2px 4px 0}
.cc-tt-item{position:relative;display:flex;align-items:center;gap:10px;padding:7px 8px 7px 10px;border-radius:8px}
.cc-tt-item+.cc-tt-item{box-shadow:inset 0 1px 0 rgba(15,23,42,0.04)}
.cc-tt-bar{flex-shrink:0;width:3px;height:18px;border-radius:2px;background:linear-gradient(180deg,var(--accent),color-mix(in srgb,var(--accent) 75%,#000));box-shadow:0 0 0 1px color-mix(in srgb,var(--accent) 25%,transparent),0 0 10px color-mix(in srgb,var(--accent) 40%,transparent)}
.cc-tt-name{flex:1;min-width:0;white-space:normal;overflow-wrap:anywhere;line-height:1.25;font-size:${fontSize}px;font-weight:500;color:#64748b}
.cc-tt-vwrap{display:inline-flex;align-items:baseline;gap:6px;margin-left:auto;padding-left:14px}
.cc-tt-value{font-variant-numeric:tabular-nums;font-feature-settings:"tnum" 1;font-weight:650;font-size:${fontSize + 2}px;color:#334155;line-height:1;letter-spacing:-0.02em}
.cc-tt-unit{font-size:${Math.max(fontSize - 1, 10)}px;font-weight:600;color:#64748b;letter-spacing:0.05em;text-transform:uppercase;align-self:center;transform:translateY(-1px);background:rgba(15,23,42,0.04);padding:1px 5px;border-radius:4px}
</style>`;

  return `<div class="cc-tt-shell"><div class="cc-tt"><div class="cc-tt-header"><span class="cc-tt-kicker"><span class="cc-tt-kicker-dot"></span>Snapshot</span><div class="cc-tt-title-stack"><span class="cc-tt-title">${escapeHtml(
    title,
  )}</span>${subtitleHtml}</div></div><div class="cc-tt-content">${items}</div></div></div>${css}`;
};

const timeToHours = (t) => {
  if (!t) return 0;
  const [h, m] = t.split(":").map(Number);
  return h + m / 60;
};

const computeReturnPeriodLabel = (rp) => {
  if (!rp?.start || !rp?.end) return null;
  const days =
    Math.round((new Date(rp.end) - new Date(rp.start)) / 86400000) + 1;
  const h = timeToHours(rp.to_time) - timeToHours(rp.from_time);
  const hLabel = h % 1 === 0 ? `${h}h` : `${h}h`;
  return `${days}d × ${hLabel}`;
};

const groupByDate = (daily = [], traderName = null) => {
  const map = {};
  daily.forEach((block) => {
    if (!map[block.date]) {
      map[block.date] = {
        date: block.date,
        scheduled_mwh: 0,
        actual_mwh: 0,
        approval_nos: block.approval_nos ?? [],
        categories: block.categories ?? [],
        trader_name: traderName,
        blocks: [],
      };
    }
    const day = map[block.date];
    day.scheduled_mwh += block.scheduled_mwh ?? 0;
    day.actual_mwh += block.actual_mwh ?? 0;
    day.blocks.push({
      block: block.time_block,
      time: block.time,
      scheduled_mwh: block.scheduled_mwh,
      actual_mwh: block.actual_mwh,
      delta_mwh: block.delta_mwh,
      delta_pct: block.delta_pct,
    });
  });
  return Object.values(map).map((day) => ({
    ...day,
    delta_mwh: +(day.actual_mwh - day.scheduled_mwh).toFixed(2),
    delta_pct: day.scheduled_mwh
      ? +(
          ((day.actual_mwh - day.scheduled_mwh) / day.scheduled_mwh) *
          100
        ).toFixed(1)
      : 0,
  }));
};

const toChartData = (daily = []) =>
  daily.map((b) => ({
    x: b.time_block ?? b.date,
    date: b.date ?? null,
    actual_mw: b.actual_mwh,
    scheduled_mw: b.scheduled_mwh,
  }));

/* ── Excel export helpers ───────────────────────────────────
   Ported from insights BankingCalculator. Two row shapes: raw 15-min
   blocks (bucketToRow) and day-grouped breakdown rows (dayRowToExcel). */
const round = (v, dec = 2) =>
  v == null || v === "" ? "" : +Number(v).toFixed(dec);

const joinList = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");

// Raw 15-minute block bucket → flat Excel row.
const bucketToRow = (d) => ({
  Date: d.date,
  "Time Block": d.time_block ?? "",
  Time: d.time ?? "",
  "Scheduled MWh": round(d.scheduled_mwh),
  "Actual MWh": round(d.actual_mwh),
  "Delta MWh": round(d.delta_mwh),
  "Delta %": round(d.delta_pct, 1),
  "Approval Nos": joinList(d.approval_nos),
  Categories: joinList(d.categories),
});

// Day-grouped breakdown row (from groupByDate) → flat Excel row.
const dayRowToExcel = (r) => ({
  Date: r.date,
  "Scheduled MWh": round(r.scheduled_mwh),
  "Actual MWh": round(r.actual_mwh),
  "Delta MWh": round(r.delta_mwh),
  "Delta %": round(r.delta_pct, 1),
  "Approval Nos": joinList(r.approval_nos),
  Categories: joinList(r.categories),
  "Trader Name": r.trader_name ?? "",
});

const appendSheet = (wb, rows, name) => {
  if (!rows?.length) return;
  // Excel sheet names cap at 31 chars and disallow : \ / ? * [ ].
  const safeName = name.replace(/[:\\/?*[\]]/g, " ").slice(0, 31);
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), safeName);
};

const downloadWorkbook = (wb, filename) => {
  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(
    new Blob([wbout], { type: "application/octet-stream" }),
    filename.replace(/[^a-zA-Z0-9_\-.]/g, "_"),
  );
};

const GROUP_BY_LABEL = GROUP_BY.reduce((acc, g) => {
  acc[g.key] = g.label;
  return acc;
}, {});

/* ── Delta display ──────────────────────────────────────── */
const DeltaVal = ({ val }) => {
  if (val == null) return <span>—</span>;
  return (
    <span
      style={{
        color:
          val < 0
            ? "var(--error-text, #e53935)"
            : "var(--secondary-color-text)",
      }}
    >
      {val > 0 ? "+" : ""}
      {fmt(val)}
    </span>
  );
};

const DeltaPct = ({ val }) => {
  if (val == null) return <span>—</span>;
  return (
    <span
      style={{
        color:
          val < 0
            ? "var(--error-text, #e53935)"
            : "var(--secondary-color-text)",
      }}
    >
      {val > 0 ? "+" : ""}
      {fmt(val, 1)}%
    </span>
  );
};

/* ── Summary Card ───────────────────────────────────────── */
const SummaryCard = ({ label, value, unit, sub, dotColor }) => (
  <div className={Styles.summaryCard}>
    <div className={Styles.summaryCardTop}>
      <span className={Styles.summaryCardLabel}>{label}</span>
      <span
        className={Styles.summaryCardDot}
        style={{ background: dotColor }}
      />
    </div>
    <div className={Styles.summaryCardValue}>
      {value} <span className={Styles.summaryCardUnit}>{unit}</span>
    </div>
    {sub != null && <div className={Styles.summaryCardSub}>{sub}</div>}
  </div>
);

/* ── Legend Chip ────────────────────────────────────────── */
/* Ported .line-legend look from insights ReMixChart: rounded clickable chip
   with a dot swatch (bar series) or short line swatch (line series). When the
   series is toggled off the swatch + text grey to #E0E0E0, matching the ref. */
const ChartLegendChip = ({ label, type, color, active, onClick }) => {
  const swatchColor = active ? color : "#E0E0E0";
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        fontSize: 12,
        cursor: "pointer",
        borderRadius: 6,
        padding: "4px 8px",
        border: "0.5px solid #dfeaf2",
        background: "#fff",
        userSelect: "none",
        color: swatchColor,
        transition: "color 0.15s",
      }}
    >
      {type === "bar" ? (
        <span
          style={{
            width: 8,
            height: 8,
            marginRight: 6,
            borderRadius: "50%",
            background: swatchColor,
            display: "inline-block",
            flexShrink: 0,
          }}
        />
      ) : (
        <span
          style={{
            width: 20,
            height: 2,
            marginRight: 6,
            background: swatchColor,
            display: "inline-block",
            verticalAlign: "middle",
            flexShrink: 0,
          }}
        />
      )}
      {label}
    </div>
  );
};

/* ── Chart Section ──────────────────────────────────────── */
const ChartSection = ({ title, groupBy, onGroupByChange, loading, data }) => {
  const isBlockMode = groupBy === "block";
  const safe = data ?? [];
  const totalPoints = safe.length;
  const chartInstanceRef = useRef(null);
  const safeRef = useRef(safe); // ← always current, no stale closure
  const [activeSeries, setActiveSeries] = useState("both");

  // Keep ref in sync every render
  safeRef.current = safe;

  /* ── Dynamic x-axis tick density: zoom tracking ───────────
     lastZoom (ref) updates instantly on every datazoom (no rerender);
     zoomRange (state) is debounced and only triggers the interval recompute.
     Never push zoomRange back into dataZoom.start/end — that fights ECharts
     mid-drag and causes jitter. */
  const lastZoom = useRef(null); // { start, end } in 0–100 percent
  const [zoomRange, setZoomRange] = useState(null);
  const zoomDebounce = useRef(null);
  const skipNextZoom = useRef(false);
  const isDragging = useRef(false);
  const dragEndTimer = useRef(null);
  const stableOptionRef = useRef(null);

  const showActual = activeSeries === "both" || activeSeries === "actual";
  const showScheduled = activeSeries === "both" || activeSeries === "scheduled";

  const toggleSeries = (name) =>
    setActiveSeries((prev) => (prev === name ? "both" : name));

  const useBlockWindow = isBlockMode && totalPoints > 96;
  const initStartValue = Math.max(0, totalPoints - 96);

  // Default window as 0–100 percent (the pattern works in percent space).
  const defaultStartPercent = useBlockWindow
    ? (initStartValue / Math.max(1, totalPoints - 1)) * 100
    : 0;
  const defaultEndPercent = 100;

  /* ── datazoom handler: instant ref update + debounced state ── */
  const handleDataZoom = useCallback((params) => {
    if (skipNextZoom.current) {
      skipNextZoom.current = false;
      return;
    }
    isDragging.current = true;
    if (dragEndTimer.current) clearTimeout(dragEndTimer.current);
    dragEndTimer.current = setTimeout(() => {
      isDragging.current = false;
    }, 200);

    const zoom = params.batch ? params.batch[0] : params;
    const start = zoom.start ?? 0;
    const end = zoom.end ?? 100;
    lastZoom.current = { start, end };

    if (zoomDebounce.current) clearTimeout(zoomDebounce.current);
    zoomDebounce.current = setTimeout(() => setZoomRange({ start, end }), 250);
  }, []);

  /* ── Reset zoom whenever data / mode changes ── */
  useEffect(() => {
    lastZoom.current = null;
    setZoomRange(null);
    skipNextZoom.current = true;
  }, [data, isBlockMode]);

  /* ── Compute the smart, window-aware x-axis interval ──────
     lastZoom (instant) feeds start/end; zoomRange (debounced) is read so a
     settled drag triggers the recompute. visibleDataCount → tick density. */
  const totalLen = totalPoints || 1;
  const zoomStart = lastZoom.current?.start ?? defaultStartPercent;
  const zoomEnd = lastZoom.current?.end ?? defaultEndPercent;
  const visibleStart = zoomRange?.start ?? zoomStart;
  const visibleEnd = zoomRange?.end ?? zoomEnd;

  // Window bounds in global array indices (used both for tick selection and to
  // re-base the window-relative formatter index — see
  // [[echarts-category-formatter-index]]).
  const windowStartIndex = Math.round(
    (visibleStart / 100) * Math.max(0, totalLen - 1),
  );
  const windowEndIndex = Math.round(
    (visibleEnd / 100) * Math.max(0, totalLen - 1),
  );

  // Distinct days visible (in order) → drives the adaptive tick tier. Each day
  // has 96 fifteen-minute blocks; the block value lives on d.x.
  const visibleDateList = [];
  const seenDates = new Set();
  for (let i = windowStartIndex; i <= windowEndIndex && i < totalLen; i++) {
    const dt = safe[i]?.date;
    if (dt && !seenDates.has(dt)) {
      seenDates.add(dt);
      visibleDateList.push(dt);
    }
  }
  const numVisibleDays = visibleDateList.length || 1;
  // Each visible date → its ordinal (0,1,2,…) so we can thin dates when wide.
  const dateOrdinal = new Map(visibleDateList.map((d, i) => [d, i]));

  /* ── Adaptive tick tiers (single intelligent axis) ────────
     WIDE   (≥16 days): dates only — NO block numbers. Date on the first tick
                        of every dateStep-th day, nothing else.
     MEDIUM (3–15 days): date is the parent; show 6-hourly blocks (1,25,49,73 —
                        every 24th block). Date prints on each day's first tick.
     ZOOMED (1–2 days):  date is the parent; every block when one day is in view,
                        every 4th (hourly) for two. Date on each day's first tick.
     The date always renders on the FIRST visible tick of its day, so it reads
     as a parent header above that day's block ticks. */
  const TIER_WIDE = "wide";
  const TIER_MEDIUM = "medium";
  const TIER_ZOOMED = "zoomed";
  const tier =
    numVisibleDays >= 16
      ? TIER_WIDE
      : numVisibleDays >= 3
        ? TIER_MEDIUM
        : TIER_ZOOMED;

  // Wide: thin dates so labels don't smear.
  const dateStep =
    numVisibleDays <= 20
      ? 2
      : numVisibleDays <= 45
        ? 4
        : Math.ceil(numVisibleDays / 10);

  // Medium/zoomed: which block numbers get a tick — anchored to the block value
  // so every day shows the same ascending set (no global-index aliasing).
  // Block-step sizing ported from insights ReMixChart's computeSmartXInterval:
  // the tick step scales *continuously* with the number of points actually
  // visible in the zoom window (per day), instead of snapping to a fixed grid.
  // This spreads ticks more robustly as you zoom — the visible count drives the
  // density smoothly rather than jumping between coarse tiers.
  const visiblePointCount = Math.max(1, windowEndIndex - windowStartIndex + 1);
  // Points visible within a single day of the current window.
  const visiblePerDay = Math.max(
    1,
    Math.round(visiblePointCount / numVisibleDays),
  );
  const computeBlockStep = (count) => {
    if (count <= 15) return 1; // every block — plenty of room
    if (count <= 30) return Math.max(1, Math.floor(count / 8) - 1);
    if (count <= 60) return Math.max(1, Math.floor(count / 8) - 1);
    if (count <= 90) return Math.max(1, Math.floor(count / 10) - 1);
    if (count <= 180) return Math.max(1, Math.floor(count / 12) - 1);
    return Math.max(1, Math.floor(count / 12) - 1);
  };
  const blockStep = tier === TIER_MEDIUM ? 24 : computeBlockStep(visiblePerDay);

  // Single day in view: render the date as a centered axis title (parent header)
  // instead of inline under block 1, so it reads centered over the block ticks.
  const singleDayDate =
    tier === TIER_ZOOMED && numVisibleDays === 1
      ? (visibleDateList[0] ?? "")
      : "";

  // Helper: is the point at global index gi the first visible point of its day?
  const isDayStart = (snapshot, gi) =>
    snapshot[gi]?.date &&
    (gi === windowStartIndex || snapshot[gi].date !== snapshot[gi - 1]?.date);

  const lastXIndex = Math.max(0, safe.length - 1);
  const xAxisIntervalFn = (index) => {
    if (index === 0 || index === lastXIndex) return true;
    const item = safe[index];
    if (!item) return false;

    if (tier === TIER_WIDE) {
      // Only the day-start of every dateStep-th visible day.
      if (!isDayStart(safe, index)) return false;
      const ord = dateOrdinal.get(item.date) ?? 0;
      return ord % dateStep === 0;
    }

    // Medium / zoomed: always tick the day-start (so the parent date has a slot),
    // plus every block on the chosen step grid.
    if (isDayStart(safe, index)) return true;
    const blockNum = Number(item.x);
    if (!Number.isFinite(blockNum)) return false;
    return (blockNum - 1) % blockStep === 0;
  };

  const xAxisLabelFormatter = (value, index) => {
    const snapshot = safeRef.current;
    const gi = windowStartIndex + index;
    const item = snapshot[gi];
    if (!item) return "";
    const dayStart = isDayStart(snapshot, gi);

    if (tier === TIER_WIDE) {
      // Date only — no block number.
      return dayStart ? `{d|${item.date}}` : "";
    }

    // Single day: date is shown as the centered axis title, so ticks are just
    // block numbers (no inline date under block 1).
    if (singleDayDate) return String(value ?? "");

    // Medium / multi-day zoomed: block number on every tick; date as parent on
    // each day's first tick.
    return dayStart ? `${value}\n{d|${item.date}}` : String(value ?? "");
  };

  const computedOptions = {
    grid: { top: 8, right: 16, bottom: isBlockMode ? 96 : 64, left: 52 },
    xAxis: {
      type: "category",
      data: safe.map((d) => d.x),
      // Axis line + ticks styled to match insights ReMixChart.
      axisLine: { lineStyle: { color: AXIS_LINE_COLOR } },
      axisTick: {
        show: true,
        alignWithLabel: true,
        length: 4,
        lineStyle: { color: AXIS_LINE_COLOR },
      },
      boundaryGap: true,
      axisLabel: {
        fontSize: 10,
        color: AXIS_LABEL_COLOR,
        margin: 12,
        showMinLabel: true,
        showMaxLabel: true,
        hideOverlap: false,
        ...(isBlockMode
          ? { interval: xAxisIntervalFn, formatter: xAxisLabelFormatter }
          : {}),
        rich: { d: { fontSize: 9, color: "#888", lineHeight: 16 } },
      },
      // Single day in view → date as a centered parent header under the axis.
      ...(singleDayDate
        ? {
            name: singleDayDate,
            nameLocation: "middle",
            nameGap: 32,
            nameTextStyle: {
              fontSize: 11,
              color: SECONDARY_COLOR,
              fontWeight: 600,
            },
          }
        : {}),
    },
    yAxis: {
      type: "value",
      splitNumber: 5,
      scale: false,
      boundaryGap: [0, 0.1],
      axisLine: { show: false },
      axisTick: {
        show: true,
        length: 4,
        lineStyle: { color: AXIS_LINE_COLOR },
      },
      splitLine: { lineStyle: { color: "#f0f0f0" } },
      axisLabel: {
        fontSize: 10,
        color: AXIS_LABEL_COLOR,
        margin: 8,
        formatter: formatAxisValue,
        hideOverlap: true,
      },
      name: isBlockMode ? "MW" : "MWh",
      nameLocation: "middle",
      nameGap: 40,
      nameRotate: 90,
      nameTextStyle: {
        fontSize: 11,
        color: SECONDARY_COLOR,
        fontWeight: 600,
        fontFamily: "Inter, system-ui, sans-serif",
      },
    },
    tooltip: {
      trigger: "axis",
      triggerOn: "mousemove",
      backgroundColor: "transparent",
      borderColor: "transparent",
      borderWidth: 0,
      padding: 0,
      // Card styling lives in the inline <style> from buildTooltipHTML; keep the
      // ECharts wrapper transparent so only the frosted card shows.
      extraCssText:
        "box-shadow:none;background:transparent;border:none;padding:0;",
      // Line axis-pointer styled to match the tooltip accent.
      axisPointer: {
        type: "line",
        snap: false,
        lineStyle: {
          color: SECONDARY_COLOR,
          width: 1,
          type: "dashed",
          opacity: 0.5,
        },
        label: { show: false },
      },
      formatter: (params) => {
        const idx = params[0]?.dataIndex ?? 0;
        const item = safeRef.current[idx]; // ← use ref here too
        const unit = isBlockMode ? "MW" : "MWh";
        const title =
          isBlockMode && item
            ? `Block ${item.x}`
            : String(params[0]?.axisValue ?? "");
        const subtitle = isBlockMode && item?.date ? item.date : "";
        const rows = params.map((p) => ({
          name: p.seriesName,
          value: p.value,
          color: p.color,
        }));
        return buildTooltipHTML({ title, subtitle, rows, unit });
      },
    },
    legend: { show: false },
    dataZoom: [
      // Never push zoomRange into start/end — only lastZoom/defaults, so we
      // don't fight ECharts during the drag (causes jitter).
      { type: "inside", start: zoomStart, end: zoomEnd, filterMode: "none" },
      {
        // Slider styled to match insights ReMixChart brush exactly: minimal
        // (ECharts defaults) + SecondaryColor text + droplet handle icon.
        type: "slider",
        labelFormatter: (val) => {
          const idx = Math.round(val);
          const arr = safeRef.current;
          if (idx < 0 || idx >= arr.length) return "";
          const d = arr[idx];
          // Slot/block number only — date intentionally omitted.
          return String(d.x ?? "");
        },
        start: zoomStart,
        end: zoomEnd,
        height: 17,
        width: "98%",
        bottom: 10,
        left: 10,
        brushSelect: false,
        textStyle: { color: SECONDARY_COLOR },
        handleIcon:
          "path://M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z",
        handleSize: "80%",
        filterMode: "none",
        throttle: 60,
      },
    ],
    series: [
      {
        name: "Actual",
        type: "bar",
        data: showActual ? safe.map((d) => d.actual_mw) : safe.map(() => null),
        itemStyle: { color: SERIES_COLORS.actual, borderRadius: [2, 2, 0, 0] },
        barMaxWidth: 14,
      },
      {
        name: "Scheduled",
        type: "line",
        data: showScheduled
          ? safe.map((d) => d.scheduled_mw)
          : safe.map(() => null),
        lineStyle: { color: SERIES_COLORS.scheduled, width: 2, type: "solid" },
        itemStyle: { color: SERIES_COLORS.scheduled },
        symbol: "none",
      },
    ],
  };

  /* During an active drag, render the last stable option so notMerge can't
     reinitialize the chart and fight the drag. Cache once drag settles. */
  const chartOptions =
    isDragging.current && stableOptionRef.current
      ? stableOptionRef.current
      : computedOptions;
  if (!isDragging.current) stableOptionRef.current = computedOptions;

  return (
    <div className={Styles.chartSection}>
      <div className={Styles.chartSectionHeader}>
        <span className={Styles.chartTitle}>{title}</span>
        <div className={Styles.periodToggle}>
          {GROUP_BY.map((g) => (
            <button
              key={g.key}
              className={`${Styles.periodToggleBtn} ${
                groupBy === g.key ? Styles.periodToggleBtnActive : ""
              }`}
              onClick={() => onGroupByChange(g.key)}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>
      <div className={Styles.chartWrapper}>
        {loading ? (
          <div className={Styles.chartPlaceholder}>Loading…</div>
        ) : !safe.length ? (
          <div className={Styles.chartPlaceholder}>No data</div>
        ) : (
          <>
            <ReactECharts
              style={{ width: "100%", height: 290 }}
              option={chartOptions}
              notMerge
              onChartReady={(instance) => {
                chartInstanceRef.current = instance;
              }}
              onEvents={{ datazoom: handleDataZoom }}
            />
            {/* Legend — ported .line-legend chip style from insights ReMixChart:
                dot swatch for the bar series, short line for the line series,
                rounded chip, click toggles the series (greys to #E0E0E0). */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 8,
                marginTop: 6,
              }}
            >
              <ChartLegendChip
                label="Actual"
                type="bar"
                color={SERIES_COLORS.actual}
                active={showActual}
                onClick={() => toggleSeries("actual")}
              />
              <ChartLegendChip
                label="Scheduled"
                type="line"
                color={SERIES_COLORS.scheduled}
                active={showScheduled}
                onClick={() => toggleSeries("scheduled")}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ── Breakdown Table ────────────────────────────────────── */
const BreakdownTable = ({ rows, expandedRows, onToggle, loading }) => {
  if (loading) return <div className={Styles.tableLoading}>Loading…</div>;
  if (!rows?.length)
    return <div className={Styles.tableLoading}>No data available</div>;

  return (
    <div className={Styles.tableWrapper}>
      <table className={Styles.breakdownTable}>
        <thead>
          <tr>
            <th className={Styles.thCell} style={{ width: 28 }} />
            <th className={Styles.thCell}>DATE</th>
            <th className={Styles.thCell}>SCHEDULED MWH</th>
            <th className={Styles.thCell}>ACTUAL MWH</th>
            <th className={Styles.thCell}>Δ MWH</th>
            <th className={Styles.thCell}>Δ %</th>
            <th className={Styles.thCell}>APPROVAL NOs.</th>
            <th className={Styles.thCell}>CATEGORIES</th>
            <th className={Styles.thCell}>TRADER NAME</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const key = row.date ?? i;
            const expanded = expandedRows.has(key);
            return (
              <React.Fragment key={key}>
                <tr
                  className={`${Styles.tdRow} ${expanded ? Styles.tdRowExpanded : ""}`}
                  onClick={() => onToggle(key)}
                >
                  <td className={Styles.tdCell}>
                    {expanded ? (
                      <ExpandMore
                        className={Styles.expandIcon}
                        style={{ color: "#1565c0" }}
                      />
                    ) : (
                      <ChevronRight className={Styles.expandIcon} />
                    )}
                  </td>
                  <td className={`${Styles.tdCell} ${Styles.tdDate}`}>
                    {row.date}
                  </td>
                  <td className={Styles.tdCell}>{fmt(row.scheduled_mwh)}</td>
                  <td className={Styles.tdCell}>{fmt(row.actual_mwh)}</td>
                  <td className={Styles.tdCell}>
                    <DeltaVal val={row.delta_mwh} />
                  </td>
                  <td className={Styles.tdCell}>
                    <DeltaPct val={row.delta_pct} />
                  </td>
                  <td className={Styles.tdCell}>
                    <div className={Styles.approvalList}>
                      {(row.approval_nos ?? []).map((n, j) => (
                        <span key={j} className={Styles.approvalNo}>
                          {n}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className={Styles.tdCell}>
                    <div className={Styles.categoryList}>
                      {(row.categories ?? [])
                        .flatMap((c) => c.split(" "))
                        .filter(Boolean)
                        .map((c, j) => (
                          <span key={j} className={Styles.categoryBadge}>
                            {c}
                          </span>
                        ))}
                    </div>
                  </td>
                  <td className={Styles.tdCell}>{row.trader_name ?? "—"}</td>
                </tr>

                {expanded &&
                  (row.blocks ?? []).map((b, j) => (
                    <tr
                      key={`${key}-b${b.block ?? j}`}
                      className={Styles.blockRow}
                    >
                      <td className={Styles.tdCell} />
                      <td className={Styles.tdCell}>
                        <div className={Styles.blockMeta}>
                          <span className={Styles.blockBadge}>{b.block}</span>
                          <span className={Styles.blockTime}>{b.time}</span>
                        </div>
                      </td>
                      <td className={Styles.tdCell}>{fmt(b.scheduled_mwh)}</td>
                      <td className={Styles.tdCell}>{fmt(b.actual_mwh)}</td>
                      <td className={Styles.tdCell}>
                        <DeltaVal val={b.delta_mwh} />
                      </td>
                      <td className={Styles.tdCell}>
                        <DeltaPct val={b.delta_pct} />
                      </td>
                      <td className={Styles.tdCell} />
                      <td className={Styles.tdCell} />
                      <td className={Styles.tdCell}>
                        {row.trader_name ?? "—"}
                      </td>
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════ */
const ContractViewContainer = ({ contractId = "342c6254" }) => {
  const dispatch = useDispatch();
  const {
    contractAnalytics: analytics,
    contractReturnAnalytics: returnAnalytics,
    contractAnalyticsLoading: supplyLoading,
    contractReturnAnalyticsLoading: returnLoading,
  } = useSelector((state) => state.bankingCalcSlice);

  const [activeTab, setActiveTab] = useState("overview");
  const [supplyGroupBy, setSupplyGroupBy] = useState("block");
  const [returnGroupBy, setReturnGroupBy] = useState("block");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedReturnRows, setExpandedReturnRows] = useState(new Set());

  useEffect(() => {
    if (!contractId) return;
    setExpandedRows(new Set());
    dispatch(
      fetchContractAnalytics({ id: contractId, groupBy: supplyGroupBy }),
    );
  }, [dispatch, contractId, supplyGroupBy]);

  useEffect(() => {
    if (!contractId) return;
    setExpandedReturnRows(new Set());
    dispatch(
      fetchContractReturnAnalytics({ id: contractId, groupBy: returnGroupBy }),
    );
  }, [dispatch, contractId, returnGroupBy]);

  const toggleRow = (key) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleReturnRow = (key) =>
    setExpandedReturnRows((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  if (!contractId) {
    return (
      <div className={Styles.viewEmpty}>
        <span className={Styles.viewEmptyText}>
          Select a contract to view analytics
        </span>
      </div>
    );
  }

  /* ── Destructure response ── */
  const contract = analytics?.contract;
  const metrics = analytics?.metrics;
  const daily = analytics?.daily ?? [];
  const rContract = returnAnalytics?.contract;
  const rMetrics = returnAnalytics?.metrics;
  const rDaily = returnAnalytics?.daily ?? [];

  /* ── Derived: supply header ── */
  const returnPeriod = contract?.return_periods?.[0];
  const supplyPeriod = contract?.supply_periods?.[0];
  const windowStart = metrics?.window_start ?? supplyPeriod?.start;
  const windowEnd = metrics?.window_end ?? supplyPeriod?.end;
  const bankingFactor =
    returnPeriod?.banking_factor_pct ?? metrics?.banking_factor_pct;

  /* ── Derived: supply summary ── */
  const supplyDeltaPct = metrics?.total_scheduled_mwh
    ? ((metrics.total_actual_mwh - metrics.total_scheduled_mwh) /
        metrics.total_scheduled_mwh) *
      100
    : null;

  const returnPeriodLabel = computeReturnPeriodLabel(returnPeriod);

  /* ── Derived: return summary ── */
  const returnRequired =
    rMetrics?.return_required_mwh ?? metrics?.return_required_mwh;
  const returnGiven = rMetrics?.total_actual_mwh ?? 0;
  const returnScheduled = rMetrics?.total_scheduled_mwh ?? returnRequired;
  const returnLeft =
    returnRequired != null ? returnRequired - returnGiven : null;

  const rReturnDays = returnPeriod
    ? Math.round(
        (new Date(returnPeriod.end) - new Date(returnPeriod.start)) / 86400000,
      ) + 1
    : 1;
  const dailyAvgReturn = rReturnDays > 0 ? returnGiven / rReturnDays : 0;

  const totalReturnBlocks = Math.round((metrics?.total_return_hours ?? 0) * 4);
  const returnAvgLabel = totalReturnBlocks
    ? `${totalReturnBlocks}d × 0.25h`
    : null;

  /* ── Derived: chart & table data ── */
  const supplyChartData = toChartData(daily);
  const returnChartData = toChartData(rDaily);
  const supplyBreakdown = groupByDate(daily, contract?.trader);
  const returnBreakdown = groupByDate(
    rDaily,
    rContract?.trader ?? contract?.trader,
  );

  /* ── Excel export ──────────────────────────────────────────
     Builds a workbook with the current-granularity Supply/Return sheets
     (raw blocks when the toggle is on Time Block, day-grouped rows otherwise),
     plus always-present raw-block sheets so every level is captured. */
  const handleExport = () => {
    if (!daily.length && !rDaily.length) return;

    const wb = XLSX.utils.book_new();
    const supplyLabel = GROUP_BY_LABEL[supplyGroupBy] ?? supplyGroupBy;
    const returnLabel = GROUP_BY_LABEL[returnGroupBy] ?? returnGroupBy;

    // Current-granularity sheets (match the section's group-by toggle).
    if (supplyGroupBy === "block") {
      appendSheet(wb, daily.map(bucketToRow), `Supply (${supplyLabel})`);
    } else {
      appendSheet(
        wb,
        supplyBreakdown.map(dayRowToExcel),
        `Supply (${supplyLabel})`,
      );
    }
    if (returnGroupBy === "block") {
      appendSheet(wb, rDaily.map(bucketToRow), `Return (${returnLabel})`);
    } else {
      appendSheet(
        wb,
        returnBreakdown.map(dayRowToExcel),
        `Return (${returnLabel})`,
      );
    }

    // Always include raw-block + day-grouped sheets so nothing is lost when a
    // section is toggled away from Time Block / Day.
    if (supplyGroupBy !== "block")
      appendSheet(wb, daily.map(bucketToRow), "Supply (Blocks)");
    appendSheet(wb, supplyBreakdown.map(dayRowToExcel), "Supply (Days)");
    if (returnGroupBy !== "block")
      appendSheet(wb, rDaily.map(bucketToRow), "Return (Blocks)");
    appendSheet(wb, returnBreakdown.map(dayRowToExcel), "Return (Days)");

    const supplier = contract?.supplier ?? "supply";
    const receiver = contract?.receiver ?? "return";
    downloadWorkbook(wb, `banking_${supplier}_${receiver}.xlsx`);
  };

  return (
    <div className={Styles.viewContainer}>
      {/* ── Header ── */}
      <div className={Styles.viewHeader}>
        <div className={Styles.viewHeaderLeft}>
          {contract && (
            <>
              <div className={Styles.partyFlow}>
                <span className={Styles.partyFlowName}>
                  {contract.supplier}
                </span>
                <ArrowRightAlt className={Styles.partyFlowArrow} />
                <span className={Styles.partyFlowName}>
                  {contract.receiver}
                </span>
              </div>
              <div className={Styles.viewMeta}>
                <span className={Styles.viewMetaItem}>
                  <span className={Styles.viewMetaKey}>LoA</span>{" "}
                  {contract.loa_number}
                </span>
                <span className={Styles.viewMetaSep}>·</span>
                <span className={Styles.viewMetaItem}>
                  <span className={Styles.viewMetaKey}>Signed</span>{" "}
                  {contract.loa_date}
                </span>
                {bankingFactor != null && (
                  <>
                    <span className={Styles.viewMetaSep}>·</span>
                    <span className={Styles.viewMetaItem}>
                      <span className={Styles.viewMetaKey}>Banking factor</span>{" "}
                      {bankingFactor}%
                    </span>
                  </>
                )}
                {windowStart && windowEnd && (
                  <>
                    <span className={Styles.viewMetaSep}>·</span>
                    <span className={Styles.viewMetaItem}>
                      <span className={Styles.viewMetaKey}>Window</span>{" "}
                      {windowStart} – {windowEnd}
                    </span>
                  </>
                )}
                {contract.trader && (
                  <>
                    <span className={Styles.viewMetaSep}>·</span>
                    <span className={Styles.viewMetaItem}>
                      <span className={Styles.viewMetaKey}>Trader Name</span>{" "}
                      {contract.trader}
                    </span>
                  </>
                )}
              </div>
            </>
          )}
        </div>
        <button
          className={Styles.exportBtn}
          onClick={handleExport}
          disabled={!daily.length && !rDaily.length}
        >
          <FileDownload style={{ fontSize: "14px" }} /> Export
        </button>
      </div>

      {/* ── Tabs ── */}
      <div className={Styles.viewTabs}>
        {["overview", "documents"].map((t) => (
          <button
            key={t}
            className={`${Styles.viewTab} ${activeTab === t ? Styles.viewTabActive : ""}`}
            onClick={() => setActiveTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {activeTab === "overview" && (
        <div className={`${Styles.overviewContent} scroll_bar_y`}>
          {/* Supply summary */}
          <div className={Styles.summaryGrid}>
            <SummaryCard
              label="SCHEDULED"
              value={fmt(metrics?.total_scheduled_mwh, 0)}
              unit="MWh"
              sub={
                metrics?.supply_days != null
                  ? `${metrics.supply_days} days`
                  : null
              }
              dotColor="#4caf50"
            />
            <SummaryCard
              label="ACTUAL SUPPLY"
              value={fmt(metrics?.total_actual_mwh, 0)}
              unit="MWh"
              sub={
                supplyDeltaPct != null ? (
                  <span style={{ color: "#e53935" }}>
                    ▼ {Math.abs(supplyDeltaPct).toFixed(2)} % vs scheduled
                  </span>
                ) : null
              }
              dotColor="#ff9800"
            />
            <SummaryCard
              label="RETURN REQUIRED"
              value={fmt(metrics?.return_required_mwh)}
              unit="MWh"
              sub={bankingFactor != null ? `${bankingFactor}% factor` : null}
              dotColor="#9c27b0"
            />
            <SummaryCard
              label="AVG RETURN"
              value={fmt(metrics?.avg_return_mw)}
              unit="MW"
              sub={returnPeriodLabel}
              dotColor="#ffc107"
            />
          </div>

          {/* Supply chart */}
          <ChartSection
            title={`Supply To ${contract?.receiver ?? ""}`}
            groupBy={supplyGroupBy}
            onGroupByChange={setSupplyGroupBy}
            loading={supplyLoading}
            data={supplyChartData}
          />

          {/* Supply period breakdown */}
          <div className={Styles.breakdownSection}>
            <div className={Styles.breakdownTitle}>Period breakdown</div>
            <div className={Styles.breakdownSub}>
              Click a day to drill into 15-minute blocks
            </div>
            <BreakdownTable
              rows={supplyBreakdown}
              expandedRows={expandedRows}
              onToggle={toggleRow}
              loading={supplyLoading}
            />
          </div>

          {/* Return divider */}
          <div className={Styles.returnDivider}>
            <span className={Styles.returnDividerLine} />
            <span className={Styles.returnDividerLabel}>RETURN</span>
            <span className={Styles.returnDividerLine} />
          </div>

          {/* Return summary */}
          <div className={Styles.summaryGrid}>
            <SummaryCard
              label="RETURN REQUIRED"
              value={fmt(returnRequired)}
              unit="MWh"
              sub={bankingFactor != null ? `${bankingFactor}% factor` : null}
              dotColor="#9c27b0"
            />
            <SummaryCard
              label="RETURN GIVEN"
              value={fmt(returnGiven)}
              unit="MWh"
              sub={
                returnScheduled != null
                  ? `Scheduled ${fmt(returnScheduled)} MWh`
                  : null
              }
              dotColor="#26a69a"
            />
            <SummaryCard
              label="RETURN LEFT"
              value={fmt(returnLeft)}
              unit="MWh"
              sub={`Daily avg ${fmt(dailyAvgReturn, 0)} MWh`}
              dotColor="#f44336"
            />
            <SummaryCard
              label="AVG RETURN"
              value={fmt(metrics?.avg_return_mw)}
              unit="MW"
              sub={returnAvgLabel}
              dotColor="#ffc107"
            />
          </div>

          {/* Return chart */}
          <ChartSection
            title={`Return To ${contract?.supplier ?? ""}`}
            groupBy={returnGroupBy}
            onGroupByChange={setReturnGroupBy}
            loading={returnLoading}
            data={returnChartData}
          />

          {/* Return breakdown */}
          <div className={Styles.breakdownSection}>
            <div className={Styles.breakdownTitle}>Return breakdown</div>
            <div className={Styles.breakdownSub}>
              Click a day to drill into 15-minute blocks
            </div>
            <BreakdownTable
              rows={returnBreakdown}
              expandedRows={expandedReturnRows}
              onToggle={toggleReturnRow}
              loading={returnLoading}
            />
          </div>
        </div>
      )}

      {activeTab === "documents" && (
        <div className={`${Styles.documentsContent} scroll_bar_y`}>
          {/* ── Section header ── */}
          <div className={Styles.docSectionHeader}>
            <div>
              <div className={Styles.docSectionTitle}>Contract Details</div>
              <div className={Styles.docSectionSub}>
                {contract?.loa_date
                  ? `Captured on ${fmtDate(contract.loa_date)}`
                  : "Contract details"}{" "}
                · Read-only snapshot of the form submission
              </div>
            </div>
            <div className={Styles.docSectionActions}>
              <button className={Styles.docActionBtn}>
                <FileDownload style={{ fontSize: 14 }} />
                Download Contract
              </button>
              <button className={Styles.docActionBtn}>
                <Edit style={{ fontSize: 14 }} />
                Edit
              </button>
            </div>
          </div>

          {/* ── Contract basics ── */}
          <div className={Styles.docBlock}>
            <div className={Styles.docBlockTitle}>Contract basics</div>
            <div className={Styles.docBasicsGrid}>
              <div className={Styles.docField}>
                <div className={Styles.docFieldLabel}>LoA Number</div>
                <div className={Styles.docFieldValue}>
                  {contract?.loa_number ?? "—"}
                </div>
              </div>
              <div className={Styles.docField}>
                <div className={Styles.docFieldLabel}>LoA Date</div>
                <div className={Styles.docFieldValue}>
                  {fmtDate(contract?.loa_date)}
                </div>
              </div>
              <div className={Styles.docField}>
                <div className={Styles.docFieldLabel}>Supplier</div>
                <div className={Styles.docFieldValue}>
                  {contract?.supplier ?? "—"}
                </div>
              </div>
              <div className={Styles.docField}>
                <div className={Styles.docFieldLabel}>Receiver</div>
                <div className={Styles.docFieldValue}>
                  {contract?.receiver ?? "—"}
                </div>
              </div>
              <div className={Styles.docField}>
                <div className={Styles.docFieldLabel}>Trader Name</div>
                <div className={Styles.docFieldValue}>
                  {contract?.trader ? (
                    <span className={Styles.docTraderBadge}>
                      {contract.trader}
                    </span>
                  ) : (
                    "—"
                  )}
                </div>
              </div>
            </div>

            {contract?.notes && (
              <div className={Styles.docNotesRow}>
                <div className={Styles.docFieldLabel}>Notes</div>
                <div className={Styles.docNotes}>{contract.notes}</div>
              </div>
            )}
          </div>

          {/* ── Supply Periods ── */}
          <div className={Styles.docBlock}>
            <div className={Styles.docBlockTitle}>
              Supply Periods{" "}
              <span className={Styles.docPeriodCount}>
                {contract?.supply_periods?.length ?? 0}
              </span>
            </div>
            <table className={Styles.docPeriodTable}>
              <thead>
                <tr>
                  {[
                    "Start Date",
                    "End Date",
                    "Quantum",
                    "Max Quantum",
                    "Start Time",
                    "End Time",
                    "Basis",
                  ].map((h) => (
                    <th key={h} className={Styles.docPeriodTh}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(contract?.supply_periods ?? []).map((sp, i) => (
                  <tr key={i} className={Styles.docPeriodRow}>
                    <td className={Styles.docPeriodTd}>{fmtDate(sp.start)}</td>
                    <td className={Styles.docPeriodTd}>{fmtDate(sp.end)}</td>
                    <td className={Styles.docPeriodTd}>
                      {sp.quantum_mw != null ? `${sp.quantum_mw} MW` : "—"}
                    </td>
                    <td className={Styles.docPeriodTd}>
                      {sp.quantum_is_max ? "Yes" : "No"}
                    </td>
                    <td className={Styles.docPeriodTd}>
                      {sp.from_time ?? "—"}
                    </td>
                    <td className={Styles.docPeriodTd}>{sp.to_time ?? "—"}</td>
                    <td className={Styles.docPeriodTd}>
                      {capitalizeFirst(sp.basis)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Return Periods ── */}
          <div className={Styles.docBlock}>
            <div className={Styles.docBlockTitle}>
              Return Period{" "}
              <span className={Styles.docPeriodCount}>
                {contract?.return_periods?.length ?? 0}
              </span>
            </div>
            <table className={Styles.docPeriodTable}>
              <thead>
                <tr>
                  {[
                    "Start Date",
                    "End Date",
                    "Quantum",
                    "Banking Factor",
                    "Start Time",
                    "End Time",
                    "Basis",
                  ].map((h) => (
                    <th key={h} className={Styles.docPeriodTh}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(contract?.return_periods ?? []).map((rp, i) => (
                  <tr key={i} className={Styles.docPeriodRow}>
                    <td className={Styles.docPeriodTd}>{fmtDate(rp.start)}</td>
                    <td className={Styles.docPeriodTd}>{fmtDate(rp.end)}</td>
                    <td className={Styles.docPeriodTd}>
                      {rp.quantum_mw != null ? `${rp.quantum_mw} MW` : "—"}
                    </td>
                    <td className={Styles.docPeriodTd}>
                      {rp.banking_factor_pct != null
                        ? `${rp.banking_factor_pct}%`
                        : "—"}
                    </td>
                    <td className={Styles.docPeriodTd}>
                      {rp.from_time ?? "—"}
                    </td>
                    <td className={Styles.docPeriodTd}>{rp.to_time ?? "—"}</td>
                    <td className={Styles.docPeriodTd}>
                      {capitalizeFirst(rp.basis)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractViewContainer;
